/**
 * Canonical analytics event names — the single source of truth shared by the
 * web and mobile apps. Import { AnalyticsEvent, AnalyticsParam } from here in
 * both platforms so event-name strings can never drift apart. TypeScript will
 * reject any name not defined below.
 */
export const AnalyticsEvent = {
  // Session lifecycle
  didStartBreathworkPractice: 'did_start_breathwork_practice',
  didEndBreathworkPractice: 'did_end_breathwork_practice',
  sessionBegun: 'session_begun',
  preSessionFeelingsSelected: 'pre_session_feelings_selected',
  postSessionFeelingsSelected: 'post_session_feelings_selected',
  sessionAbandoned: 'session_abandoned',
  practiceReminderSetUp: 'practice_reminder_set_up',

  // Breathwork browsing
  viewBreathworks: 'view_breathworks',
  viewBreathwork: 'view_breathwork',
  viewBreathworkInfo: 'view_breathwork_info',
  practicePranayamaTapped: 'practice_pranayama_tapped',
  repeatBreathworkPractice: 'repeat_breathwork_practice',
  quickStartTapped: 'quick_start_tapped',

  // Journeys
  viewJourneys: 'view_journeys',
  viewJourney: 'view_journey',
  journeyStarted: 'journey_started',
  journeyAbandoned: 'journey_abandoned',

  // Activity
  viewActivities: 'view_activities',
  viewActivity: 'view_activity',

  // Achievements
  viewAchievements: 'view_achievements',
  viewAchievement: 'view_achievement',

  // Settings
  viewSettings: 'view_settings',

  // Wizard
  didTapWizardButton: 'did_tap_wizard_button',
  viewWizardStep1: 'view_wizard_step_1',
  viewWizardStep2: 'view_wizard_step_2',
  viewWizardStep3: 'view_wizard_step_3',
  viewWizardStep4: 'view_wizard_step_4',
  viewWizardResults: 'view_wizard_results',
  selectWizardResult: 'select_wizard_result',

  // Search & filter
  search: 'search',
  filter: 'filter',
  filterItems: 'filter_items',
  viewFilterResults: 'view_filter_results',
  filtersOpened: 'filters_opened',
  filtersApplied: 'filters_applied',

  // Auth
  login: 'login',
  signUp: 'sign_up',
  googleSignInTapped: 'google_sign_in_tapped',
  passwordResetRequested: 'password_reset_requested',
  passwordResetCompleted: 'password_reset_completed',

  // Onboarding
  onboardingGoalSelected: 'onboarding_goal_selected',
  onboardingSkipped: 'onboarding_skipped',

  // Premium / upgrade
  premiumGateShown: 'premium_gate_shown',
  upgradeTapped: 'upgrade_tapped',
  trialStarted: 'trial_started',
  purchaseInitiated: 'purchase_initiated',
  purchaseCompleted: 'purchase_completed',
  purchaseCancelled: 'purchase_cancelled',
  purchaseFailed: 'purchase_failed',

  // Notifications
  notificationOpen: 'notification_open',
  pushNotificationsAuthorized: 'push_notifications_authorized',
  pushNotificationsDenied: 'push_notifications_denied',

  // Beta messaging
  betaBannerView: 'beta_banner_view',
  betaBannerDismiss: 'beta_banner_dismiss',
  appBannerView: 'app_banner_view',
  appBannerClick: 'app_banner_click',
  appBannerDismiss: 'app_banner_dismiss',

  // Sharing & referral
  shareInitiated: 'share_initiated',
  shareCopiedFallback: 'share_copied_fallback',
  referralLinkVisit: 'referral_link_visit',
  referralSignUp: 'referral_signup',

  // Tracking consent
  trackingAuthorized: 'tracking_authorized',
  trackingDenied: 'tracking_denied',

  // Generic / Firebase standard
  screenView: 'screen_view',
  share: 'share',
  selectContent: 'select_content',
  selectItem: 'select_item',
  viewItem: 'view_item',
  viewItemList: 'view_item_list',
  mainScreenAppeared: 'main_screen_appeared',

  // Contact
  contactFormSubmitted: 'contact_form_submitted',

  // Landing page
  landingHeroCtaClick: 'landing_hero_cta_click',
  landingJourneyCardClick: 'landing_journey_card_click',
  landingExploreJourneysClick: 'landing_explore_journeys_click',
  landingBreathworkCardClick: 'landing_breathwork_card_click',
  landingBreathworkSeeAllClick: 'landing_breathwork_see_all_click',
  landingFaqToggle: 'landing_faq_toggle',
  landingClosingCtaClick: 'landing_closing_cta_click',
  landingConfigurabilityStartClick: 'landing_configurability_start_click',
  landingAppStoreClick: 'landing_app_store_click',
  landingPlayStoreClick: 'landing_play_store_click',

  // Experiments
  experimentAssigned: 'experiment_assigned',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

export const AnalyticsUserProperty = {
  onboardingGoal: 'onboarding_goal',
  webVersion: 'web_version',
  subscriptionStatus: 'subscription_status',
  signupPlatform: 'signup_platform',
  lastPlatform: 'last_platform',
} as const;

export const AnalyticsParam = {
  id: 'id',
  breathworkId: 'breathwork_id',
  journeyId: 'journey_id',
  achievementId: 'achievement_id',
  packageId: 'package_id',
  query: 'query',
  screenName: 'screen_name',
  screenClass: 'screen_class',
  contentGroup: 'content_group',
  isPremiumUser: 'is_premium_user',
  source: 'source',
  contentType: 'content_type',
  referrerUid: 'referrer_uid',
  experimentName: 'experiment_name',
  experimentVariant: 'experiment_variant',
  trialType: 'trial_type',
} as const;
